import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Headset, Send, Loader2, CheckCircle2, Bot, User, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const STATUS_META = {
  ai: { label: 'AI handling', cls: 'bg-violet-500/10 text-violet-600 border-violet-500/20' },
  handoff: { label: 'Handoff', cls: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  rep: { label: 'You', cls: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' },
  resolved: { label: 'Resolved', cls: 'bg-slate-500/10 text-slate-500 border-slate-500/20' },
};

export default function LiveConversations() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [convo, setConvo] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const loadList = useCallback(async () => {
    try {
      const res = await api.get('/api/comms/conversations');
      setList(res.data?.conversations || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  const loadConvo = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const res = await api.get(`/api/comms/conversations/${sid}`);
      setConvo(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  // Poll list + open thread every 5s for near real-time (paused when tab hidden).
  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      loadList();
      if (active) loadConvo(active);
    }, 5000);
    return () => clearInterval(id);
  }, [active, loadList, loadConvo]);

  useEffect(() => { if (active) loadConvo(active); }, [active, loadConvo]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [convo]);

  const sendReply = async () => {
    const text = input.trim();
    if (!text || !active || sending) return;
    setInput('');
    setSending(true);
    try {
      await api.post(`/api/comms/conversations/${active}/reply`, { message: text });
      await loadConvo(active);
      await loadList();
    } catch { toast.error('Failed to send'); } finally { setSending(false); }
  };

  const resolve = async () => {
    if (!active) return;
    try {
      await api.put(`/api/comms/conversations/${active}/status`, { status: 'resolved' });
      toast.success('Conversation resolved');
      await loadList(); await loadConvo(active);
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="live-conversations-page">
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center text-emerald-500 bg-emerald-500/10">
            <Headset className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Live Conversations</h1>
            <p className="text-xs text-muted-foreground">Take over website chats from the AI in real time</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={loadList} data-testid="refresh-conversations-btn"><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* List */}
        <div className="w-80 border-r overflow-y-auto" data-testid="conversations-list">
          {loading ? (
            <div className="p-6 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
          ) : list.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No website chats yet. They'll appear here as visitors start chatting.</div>
          ) : list.map((c) => {
            const meta = STATUS_META[c.status] || STATUS_META.ai;
            return (
              <button
                key={c.session_id}
                data-testid={`conversation-item-${c.session_id}`}
                onClick={() => setActive(c.session_id)}
                className={`w-full text-left px-4 py-3 border-b hover:bg-muted/40 transition-colors ${active === c.session_id ? 'bg-muted/60' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm truncate">{c.lead_name}</span>
                  {c.needs_reply && <span className="h-2 w-2 rounded-full bg-rose-500 shrink-0" data-testid="needs-reply-dot" />}
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">{c.last_message || '—'}</p>
                <Badge variant="outline" className={`mt-1.5 text-[10px] ${meta.cls}`}>{meta.label}</Badge>
              </button>
            );
          })}
        </div>

        {/* Thread */}
        <div className="flex-1 flex flex-col">
          {!convo ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Select a conversation to view & reply</div>
          ) : (
            <>
              <div className="flex items-center justify-between px-5 py-3 border-b">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{convo.lead_name}</span>
                  {convo.rep_takeover && <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/20">You're handling this</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  {convo.lead_id && (
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/leads/${convo.lead_id}`)} data-testid="open-lead-btn">
                      <ExternalLink className="h-4 w-4 mr-1" />Lead
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={resolve} data-testid="resolve-conversation-btn">
                    <CheckCircle2 className="h-4 w-4 mr-1" />Resolve
                  </Button>
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="conversation-thread">
                {(convo.messages || []).map((m, i) => {
                  const isUser = m.role === 'user';
                  const isRep = m.sender === 'rep';
                  return (
                    <div key={i} className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${isUser ? 'bg-muted text-foreground' : isRep ? 'bg-emerald-600 text-white' : 'bg-violet-600 text-white'}`}>
                        <div className="flex items-center gap-1 text-[10px] opacity-80 mb-0.5">
                          {isUser ? <><User className="h-3 w-3" />Visitor</> : isRep ? <><User className="h-3 w-3" />{m.rep_name || 'Rep'}</> : <><Bot className="h-3 w-3" />AI</>}
                        </div>
                        <div className="whitespace-pre-wrap">{m.content}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-2 p-3 border-t">
                <input
                  data-testid="rep-reply-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  placeholder="Type your reply to the visitor…"
                  className="flex-1 rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/40"
                />
                <Button onClick={sendReply} disabled={sending || !input.trim()} data-testid="rep-reply-send" className="bg-emerald-600 hover:bg-emerald-700">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
